import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterItem,
  CharacterSheetInterface,
  CharacterSpell,
  DandDFeatureSource,
  EldritchInvocation,
  Feat,
  PseudoItem,
  SummonOption2,
  TransformOption2,
} from "@dndModels";
import { DisablableOption, ModelBase } from "@models";

import DandDAPI from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import DisplayObject from "@dndComponents/display/DisplayObject";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

interface Props {
  char: CharacterSheetInterface;
  obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem | TransformOption2;
  selected: boolean;
  selected_count: number;
  variant_selected: boolean;
  selected_elsewhere: boolean;
  disabled_reasons: string;
  total_selected: number;
  max?: number;
  one_mode: boolean;
  log_it?: boolean;
  onChange: (new_count: number) => void;
  getName?: (obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem | TransformOption2) => string;
  getDetails?: (obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem | TransformOption2) => string;
  getSmallDetails?: (obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem | TransformOption2) => string;
  extra_options?: (obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem | TransformOption2) => React.ReactChild;
  hide_add?: boolean;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterListOptionsRow({
  char,
  obj,
  selected,
  selected_count,
  variant_selected,
  selected_elsewhere,
  disabled_reasons,
  total_selected,
  max,
  one_mode,
  log_it = false,
  onChange,
  getName,
  getDetails,
  getSmallDetails,
  extra_options,
  hide_add = false
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const useStyles = makeStyles()((theme) => {
    if (obj instanceof DandDFeatureSource) {
      const colors = char_util.getColors({ char: char, user: api.user, for_id: obj._id, source: obj });
      return ({
        root: {
          backgroundColor: colors.backgroundColor,
          // border: "1px solid #777777",
          color: colors.color,
          // padding: "4px",
          // margin: "4px",
          // '&:hover': {
          //   backgroundColor: "Black",
          //   color: theme.palette.secondary.light
          // },
        }
      });
    } else {
      // console.log(theme);
      return ({
        root: {
          backgroundColor: theme.palette.secondary.main
        },
      });
    }
  });
  
  const useStyles2 = makeStyles()((theme) => {
    return ({
      root: {
        '&.prepared': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.secondary.dark,
        },
        '&.unprepared': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.dark,
        },
      },
    });
  });

  const renderButton = () => {
    if (log_it) {
      debugger;
    }
    let name = selected ? "Remove" : (one_mode ? "Select" : (max && total_selected >= max ? "Max Reached" : "Add"));
    if (selected_elsewhere) {
      name += " (Used Elsewhere)";
    }
    if (variant_selected) {
      name += " (Variant Used)";
    }
    // console.log(cx(classes.root, "prepared"));
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          { selected ?
            <DandDButtonBox
              name={ name }
              className={ cx2(classes2.root, "prepared") }
              onClick={() => {
                onChange(0);
              }}
            />
          : one_mode ?
            <DandDButtonBox
              name={ name }
              // className={ selected ? cx(classes.root, "prepared") : cx(classes.root, "unprepared") }
              disabled={ selected_elsewhere || variant_selected || disabled_reasons.length > 0 }
              onClick={() => {
                onChange(1);
              }}
            />
          :
            <DandDButtonBox
              name={ name }
              // className={ selected ? cx(classes.root, "prepared") : cx(classes.root, "unprepared") }
              disabled={ selected_elsewhere || variant_selected || (max && total_selected >= max) || disabled_reasons.length > 0 }
              onClick={() => {
                onChange(1);
              }}
            />
          }
        </Grid>
        { disabled_reasons.length > 0 &&
          <Grid
            className="centered small_label red"
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements display_string={disabled_reasons} cfs={char} />
          </Grid>
        }
      </Grid>
    );
  }

  const { classes, cx } = useStyles();
  const { classes: classes2, cx: cx2 } = useStyles2();

  const renderRepeatOptions = () => {
    if (max && (obj instanceof EldritchInvocation || obj instanceof Feat) && obj.repeatable && selected) {
      const options: DisablableOption[] = [];
      options.push({ id: "0", text: "0" });

      for (let i = 0; i < max; ++i) {
        if (i < selected_count || (total_selected - selected_count + i) < max) {
          options.push({ id: `${i+1}`, text: `${i+1}` });
        } else {
          options.push({ id: `${i+1}`, text: `${i+1}`, disabled: true });
        }
      }
      return (
        <>
          <Grid
            size={{
              xs: 12
            }}>
            How many times do you want to choose this?
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name=""
              options={options}
              value={`${selected_count}`}
              onChange={(changed: string) => {
                // debugger;
                onChange(+changed);
              }}
            />
          </Grid>
        </>
      );
    }
    return null;
  }
  
  // debugger;
  return (
    <Grid container spacing={0} direction="row"
      className={cx(classes.root)}>
      <Grid
        container
        spacing={0}
        direction="row"
        className="padded"
        onClick={() => {
          setExpanded(!expanded);
        }}
        size={{
          xs: 6
        }}>
        <Grid
          size={{
            xs: 12
          }}>
          { getName ? getName(obj) : <DisplayObject obj={obj} display_variant /> }
        </Grid>
        { getSmallDetails &&
          <Grid
            className="small_label gray"
            size={{
              xs: 12
            }}>{ getSmallDetails(obj) }</Grid>
        }
      </Grid>
      <Grid
        style={{ margin: "auto" }}
        size={{
          xs: 6
        }}>
        { !reloading && !hide_add && renderButton() }
      </Grid>
      { expanded && (getDetails || obj.description.length > 0) &&
        <Grid
          style={{ border: "1px solid", padding: "4px", backgroundColor: "black" }}
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements display_string={ getDetails ? getDetails(obj) : obj.description } cfs={char} />
            </Grid>
            { extra_options && extra_options(obj) }
            { renderRepeatOptions() }
          </Grid>
        </Grid>
      }
    </Grid>
  );
}
